import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Table, Tag } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { toast } from 'react-toastify';
import cx from 'classnames';

import { FaTrash as DeleteIcon, FaEye as ViewIcon } from 'react-icons/fa';

import { Order } from '../../../../models/Order/Order';
import { Pagination } from '../../../../models/contracts';

import ConfirmationModal from '../../../components/Modals/ConfirmationModal/ConfirmationModal';

const OrderStatusClasses: { [key: string]: string } = {
  pending: 'cs-pending-status',
  accepted: 'cs-accepted-status',
  declined: 'cs-declined-status',
  rented: 'cs-rented-status',
  returned: 'cs-returned-status',
  'client-accepted': 'cs-accepted-status',
  'client-declined': 'cs-declined-status'
};

const OrderStatusLabels: { [key: string]: string } = {
  pending: 'Na čekanju',
  accepted: 'Prihvaćeno',
  declined: 'Odbijeno',
  rented: 'Izdato',
  returned: 'Vraćeno',
  'client-accepted': 'Prihvaćeno od klijenta',
  'client-declined': 'Odbijeno od klijenta'
};

function OrdersList() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Order | null>(null);
  const [deleting, setDeleting] = useState(false);

  const navigate = useNavigate();

  const fetchOrders = async (page: number = 1) => {
    try {
      const response = await Order.getAll({ page });
      setOrders(response.data);
      setPagination(response.pagination);
    } catch (error) {
      toast.error('Failed to fetch orders.');
      console.error(error);
    }
  };

  useEffect(() => {
    fetchOrders(1);
  }, []);

  // Open confirmation modal
  const openDeleteModal = (order: Order) => {
    setDeleteTarget(order);
    setShowDeleteModal(true);
  };

  // Confirm deletion
  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await Order.delete(deleteTarget.id);
      toast.success('Narudžbina uspešno obrisana!');
      setShowDeleteModal(false);
      setDeleteTarget(null);
      fetchOrders(pagination?.current_page || 1);
    } catch (error) {
      toast.error('Greška pri brisanju narudžbine.');
    } finally {
      setDeleting(false);
    }
  };

  const columns: ColumnsType<Order> = [
    {
      title: 'Br.',
      dataIndex: 'number_of_order',
      key: 'number_of_order'
    },
    {
      title: 'Ime',
      dataIndex: 'full_name',
      key: 'full_name'
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag
          className={cx(
            OrderStatusClasses[status] || 'cs-default-status',
            'cs-status-badge'
          )}
        >
          {OrderStatusLabels[status] || status.replace('-', ' ')}
        </Tag>
      )
    },
    {
      title: 'Ukupna Cena',
      dataIndex: 'total_price',
      key: 'total_price',
      render: (price) => (
        <span>{price ? `${price.toLocaleString()} RSD` : '-'}</span>
      )
    },
    {
      title: 'Actions',
      dataIndex: 'id',
      key: 'actions',
      render: (id: string, row: Order) => (
        <div className="cs-action-wrapper">
          <Link
            className="acction-btn"
            onClick={() => navigate(`/admin/orders/${id}`)}
            to={`/admin/orders/${id}`}
          >
            <ViewIcon />
          </Link>
          <button
            className="cs-delete-button"
            onClick={() => openDeleteModal(row)}
          >
            <DeleteIcon />
          </button>
        </div>
      )
    }
  ];

  return (
    <div className="cs-inner-container">
      <h1 className="cs-title mb-4">Lista Narudžbina</h1>
      <Table
        columns={columns}
        rowKey="id"
        dataSource={orders}
        className="cs-table-wrapper"
        pagination={{
          current: pagination?.current_page,
          pageSize: pagination?.size,
          total: pagination?.total,
          onChange: (page) => fetchOrders(page)
        }}
      />
      <ConfirmationModal
        show={showDeleteModal}
        title="Potvrdi brisanje"
        message={
          deleteTarget
            ? `Da li ste sigurni da želite da obrišete narudžbinu "${deleteTarget.number_of_order}"?`
            : ''
        }
        confirmLabel={deleting ? 'Brišem...' : 'Obriši'}
        cancelLabel="Prekini"
        onConfirm={confirmDelete}
        onCancel={() => setShowDeleteModal(false)}
      />
    </div>
  );
}

export default OrdersList;
